import type { ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';
import * as m from '../paraglide/messages.js';
import { MoreVerticalIcon } from './icons.js';

export type TargetTileOverflowMenuProps = {
  readonly menuId: string;
  readonly open: boolean;
  readonly disabled?: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly children: ReactNode;
};

export function TargetTileOverflowMenu(props: TargetTileOverflowMenuProps): React.ReactElement {
  const { menuId, open, disabled, onOpenChange, children } = props;
  const rootRef = useRef<HTMLDivElement>(null);
  const isDisabled = disabled === true;

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    function onPointerDown(event: MouseEvent): void {
      const root = rootRef.current;
      if (root === null || root.contains(event.target as Node)) {
        return;
      }
      onOpenChange(false);
    }
    function onKeyDown(event: globalThis.KeyboardEvent): void {
      if (event.key === 'Escape') {
        onOpenChange(false);
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onOpenChange]);

  return (
    <div className="studio-target-tile-menu" ref={rootRef}>
      <button
        type="button"
        className="studio-target-tile-menu-trigger"
        disabled={isDisabled}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={open ? `studio-target-tile-menu-${menuId}` : undefined}
        aria-label={m.targetTileMenuLabel()}
        title={m.targetTileMenuLabel()}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onOpenChange(!open);
        }}
      >
        <MoreVerticalIcon />
      </button>
      {open ? (
        <div
          id={`studio-target-tile-menu-${menuId}`}
          className="studio-target-tile-menu-panel"
          role="menu"
        >
          {children}
        </div>
      ) : undefined}
    </div>
  );
}

export type TargetTileOverflowMenuItemProps = {
  readonly disabled?: boolean;
  readonly danger?: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
};

export function TargetTileOverflowMenuItem(
  props: TargetTileOverflowMenuItemProps
): React.ReactElement {
  const isDisabled = props.disabled === true;
  const isDanger = props.danger === true;
  return (
    <button
      type="button"
      className={
        isDanger ? 'studio-target-tile-menu-item is-danger' : 'studio-target-tile-menu-item'
      }
      role="menuitem"
      disabled={isDisabled}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (isDisabled) {
          return;
        }
        props.onClick();
      }}
    >
      {props.children}
    </button>
  );
}
